import React from "react";
import {
  Box,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { Event as EventIcon } from "@mui/icons-material";
import { formatPassExpiry } from "../utils/formatters";

const PassSelector = ({ passes, selectedPass, onPassChange }) => {
  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 600 }}>
        🎫 Wybierz Karnet
      </FormLabel>
      <RadioGroup
        name="passes"
        value={selectedPass || ""}
        onChange={(e) => onPassChange(e.target.value)}
      >
        <Stack spacing={1}>
          {passes.map((p) => {
            const exp = formatPassExpiry(p.valid_until);
            const isEventPass = p.is_event_pass || false;
            
            const hasUnlimitedLessons = p.lesson_categories_for_pass
              ? p.lesson_categories_for_pass.some(category => category.is_unlimited)
              : false;

            const totalRemainingLessons = p.lesson_categories_for_pass
              ? p.lesson_categories_for_pass.reduce((sum, category) => sum + category.remaining_lessons, 0)
              : 0;

            // For event passes, get event names
            const eventNames = isEventPass && p.events_for_pass
              ? p.events_for_pass.map(e => e.event_name).filter(Boolean).join(", ")
              : null;

            return (
              <Card 
                key={p.id} 
                elevation={0}
                sx={{ 
                  border: selectedPass === p.id 
                    ? isEventPass ? '2px solid #ff9800' : '2px solid #2196f3' 
                    : '1px solid #e0e0e0',
                  backgroundColor: selectedPass === p.id 
                    ? isEventPass ? '#fff3e0' : '#e3f2fd' 
                    : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: isEventPass ? '#ff9800' : '#2196f3',
                  }
                }}
              >
                <FormControlLabel
                  value={p.id}
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      {isEventPass ? (
                        // Event pass display - no lesson counter
                        <>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <Chip 
                              icon={<EventIcon sx={{ fontSize: 14 }} />}
                              label="Karnet Wydarzeniowy"
                              size="small"
                              sx={{ 
                                height: 20, 
                                fontSize: 11,
                                backgroundColor: '#ff9800',
                                color: 'white',
                                '& .MuiChip-icon': { color: 'white' }
                              }}
                            />
                          </Stack>
                          <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                            {p.pass_type_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Ważny na wszystkie zajęcia w wydarzeniu
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ważny do: {exp}
                          </Typography>
                        </>
                      ) : (
                        // Regular pass display - with lesson counter
                        <>
                          <Typography variant="body2" fontWeight="600">
                            {hasUnlimitedLessons 
                              ? "🔓 Bez limitu" 
                              : `${totalRemainingLessons} zajęć`
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ważny do: {exp}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                  sx={{ 
                    m: 0, 
                    p: 1.5, 
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      width: '100%'
                    }
                  }}
                />
              </Card>
            );
          })}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
};

export default PassSelector;
